// Phase I0.4 — Super Acca category / layer / tier enums.
//
// Source of truth for the four market categories (OUTCOME / DISCIPLINE /
// TERRITORY / PLAYER), the four leg layers (Foundation / Amplifier / Max /
// BYO), and the three slip tiers (Conservative / Standard / Maximum).
// Mirrors prototype/data.jsx::CATEGORY/LAYER/TIERS verbatim. Helpers are
// pure and unit-tested in categories.test.ts.

export const CATEGORY = {
  OUTCOME: { id: "outcome", label: "OUTCOME", short: "OUT", color: "#5D9ABF", glyph: "◆" },
  DISCIPLINE: { id: "discipline", label: "DISCIPLINE", short: "DSC", color: "#BF5D5D", glyph: "◐" },
  TERRITORY: { id: "territory", label: "TERRITORY", short: "TER", color: "#5DBF8A", glyph: "◇" },
  PLAYER: { id: "player", label: "PLAYER", short: "PLR", color: "#D4A843", glyph: "★" },
} as const;

export const LAYER = {
  FOUNDATION: { id: "foundation", label: "FOUNDATION", tone: "#5DBF8A" },
  AMPLIFIER: { id: "amplifier", label: "AMPLIFIER", tone: "#D4A843" },
  MAX: { id: "max", label: "MAX", tone: "#BF5D5D" },
  BYO: { id: "byo", label: "BUILD-YOUR-OWN", tone: "#5D9ABF" },
} as const;

export const TIERS = {
  conservative: {
    id: "conservative",
    label: "CONSERVATIVE",
    leg_range: "4–5",
    odds_target: "15–50×",
    min_conf: 75,
    stake: "GH₵100–500",
  },
  standard: {
    id: "standard",
    label: "STANDARD",
    leg_range: "6–7",
    odds_target: "50–150×",
    min_conf: 70,
    stake: "GH₵50–200",
  },
  maximum: {
    id: "maximum",
    label: "MAXIMUM",
    leg_range: "8–10",
    odds_target: "150–500×",
    min_conf: 68,
    stake: "GH₵25–100",
  },
} as const;

export type CategoryId = keyof typeof CATEGORY;
export type LayerId = keyof typeof LAYER;
export type TierId = keyof typeof TIERS;

/** Conservative is weakest, Maximum is strongest. */
export const TIER_HIERARCHY: readonly TierId[] = ["conservative", "standard", "maximum"] as const;

const FALLBACK_CATEGORY_COLOR = "#5C5A57"; // --sa-fg-muted equivalent
const FALLBACK_LAYER_TONE = "#5C5A57";

/**
 * Returns true when a slip's tier meets or exceeds the minimum required.
 * `standard` meets `standard`; `maximum` meets `conservative`. Unknown
 * inputs (defensive) return false.
 */
export function tierMeetsMinimum(slipTier: TierId, minimum: TierId): boolean {
  const slipIdx = TIER_HIERARCHY.indexOf(slipTier);
  const minIdx = TIER_HIERARCHY.indexOf(minimum);
  if (slipIdx < 0 || minIdx < 0) return false;
  return slipIdx >= minIdx;
}

/** Lookup helper — accepts upper or lower-case ids. Falls back to muted grey. */
export function categoryColor(id: string | null | undefined): string {
  if (!id) return FALLBACK_CATEGORY_COLOR;
  const upper = id.toUpperCase() as CategoryId;
  const c = CATEGORY[upper];
  return c ? c.color : FALLBACK_CATEGORY_COLOR;
}

/** Lookup helper for the single-glyph category mark. */
export function categoryGlyph(id: string | null | undefined): string {
  if (!id) return "·";
  const upper = id.toUpperCase() as CategoryId;
  const c = CATEGORY[upper];
  return c ? c.glyph : "·";
}

/** Lookup helper for the layer accent tone (used by LegRow / pill borders). */
export function layerTone(id: string | null | undefined): string {
  if (!id) return FALLBACK_LAYER_TONE;
  const upper = id.toUpperCase() as LayerId;
  const l = LAYER[upper];
  return l ? l.tone : FALLBACK_LAYER_TONE;
}
